/**
 * Event attendance date keys and options (Studio portal).
 * Keep behavior aligned with:
 * - Backend: `enrollio-be/src/events/utils/selected-attendance-dates.ts`
 * - Parent portal: `enrollio-fe/src/features/student-detail-page/attendance-date-selection.ts`
 */
import { format } from 'date-fns';

import type { GetEventResponse } from '~entities/events/api/get-event';

type EventDateSource = Pick<
  GetEventResponse,
  | 'schedules'
  | 'availability'
  | 'startDate'
  | 'endDate'
  | 'tuitionBillingCycle'
  | 'oneTime'
  | 'allowSpecificDatesSelection'
>;

export type AttendanceOption = {
  key: string;
  isoDate: string;
  label: string;
};

const DAY_MAP: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

const normalizeToUtcDate = (value: string | Date) => {
  const date = value instanceof Date ? value : new Date(value);
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
  );
};

export const toAttendanceDateKey = (value: string | Date) =>
  normalizeToUtcDate(value).toISOString().slice(0, 10);

const uniqueDates = (dates: Date[]) => {
  const seen = new Set<string>();
  return dates.filter((date) => {
    const key = toAttendanceDateKey(date);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getOccurrencesInRange = (
  startDate: string,
  endDate: string,
  targetDay: string,
) => {
  const dayIndex = DAY_MAP[targetDay];
  if (dayIndex === undefined) return [];

  const current = normalizeToUtcDate(startDate);
  const end = normalizeToUtcDate(endDate);

  while (current.getUTCDay() !== dayIndex) {
    current.setUTCDate(current.getUTCDate() + 1);
  }

  const occurrences: Date[] = [];
  while (current <= end) {
    occurrences.push(new Date(current));
    current.setUTCDate(current.getUTCDate() + 7);
  }
  return occurrences;
};

const todayUtc = () => {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
};

const getEventAttendanceDates = (event: EventDateSource): Date[] => {
  const today = todayUtc();
  let dates: Date[] = [];

  if (event.schedules && event.schedules.length > 0) {
    dates = uniqueDates(event.schedules.map(normalizeToUtcDate));
  } else if (event.availability?.length && event.startDate && event.endDate) {
    dates = uniqueDates(
      event.availability.flatMap((slot) =>
        getOccurrencesInRange(event.startDate, event.endDate, slot.day),
      ),
    );
  }

  return dates
    .filter((d) => d >= today)
    .sort((a, b) => a.getTime() - b.getTime());
};

export const getAttendanceOptions = (event: EventDateSource): AttendanceOption[] =>
  getEventAttendanceDates(event).map((date) => ({
    key: toAttendanceDateKey(date),
    isoDate: date.toISOString(),
    label: format(date, 'EEE, MMM d, yyyy'),
  }));

export const shouldAllowAttendanceDateSelection = (event: EventDateSource) =>
  event.allowSpecificDatesSelection === true &&
  getAttendanceOptions(event).length > 1;

/** Matches backend `scaleEventTuitionBySelectedDates` / parent portal `scaleAttendanceTuition`. */
export function scaleAttendanceTuition(
  tuitionFee: number,
  totalDates: number,
  selectedDates: number,
): number {
  if (totalDates === 0) {
    return tuitionFee;
  }
  if (selectedDates === 0) {
    return 0;
  }
  if (selectedDates >= totalDates) {
    return tuitionFee;
  }
  return Number(((tuitionFee / totalDates) * selectedDates).toFixed(2));
}

/** How many future occurrence slots the student is paying for (full schedule = all options). */
export function effectiveAttendanceSlotCount(
  currentIsoDates: string[],
  options: AttendanceOption[],
): number {
  const total = options.length;
  if (!total) {
    return 0;
  }
  if (!currentIsoDates.length) {
    return total;
  }
  const keySet = new Set(options.map((o) => o.key));
  const matched = currentIsoDates
    .map((d) => toAttendanceDateKey(d))
    .filter((k) => keySet.has(k)).length;
  return matched > 0 ? matched : total;
}

/** Normalize event ref from student.events[] (string, ObjectId, or populated doc). */
export const eventRefIdToString = (eventId: unknown): string => {
  if (eventId == null) return '';
  if (typeof eventId === 'string') return eventId;
  if (typeof eventId === 'object' && eventId !== null && '_id' in eventId) {
    const inner = (eventId as { _id: unknown })._id;
    return inner != null ? String(inner) : '';
  }
  return String(eventId);
};

export const getStudentSelectedDatesForEvent = (
  student: {
    events?: Array<{
      eventId: string | { _id: string } | unknown;
      selectedScheduleDates?: Array<string | Date>;
    }>;
  },
  eventId: string,
): string[] => {
  const want = String(eventId);
  const entry = student.events?.find((e) => eventRefIdToString(e.eventId) === want);
  const raw = entry?.selectedScheduleDates;
  if (!raw?.length) return [];
  return raw.map((d) => (typeof d === 'string' ? d : new Date(d).toISOString()));
};
